import React, { memo } from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { Text } from '../../ui/AppText';
import { LinearGradient } from 'expo-linear-gradient';
import Ionicons from '@expo/vector-icons/Ionicons';
import Svg, { Circle, Defs, LinearGradient as SvgLinearGradient, Stop } from 'react-native-svg';
import {
    SLIM_HEIGHT,
    CARD_FONTS,
    CARD_PADDING,
    CARD_RADIUS,
    CARD_SHADOW
} from './cardConstants';

const STUDY_PROGRESS_DONE = 8;
const STUDY_PROGRESS_TOTAL = 12;
const STUDY_RING_SIZE = 42;
const STUDY_RING_STROKE = 4;
const STUDY_RING_RADIUS = (STUDY_RING_SIZE - STUDY_RING_STROKE) / 2;
const STUDY_RING_CIRCUMFERENCE = 2 * Math.PI * STUDY_RING_RADIUS;
const STUDY_RING_OFFSET = STUDY_RING_CIRCUMFERENCE * (1 - STUDY_PROGRESS_DONE / STUDY_PROGRESS_TOTAL);

const STUDY_TOPICS = [
    { name: 'Direito Constit.', time: '2h', color: '#10B981' },
    { name: 'Português', time: '5h', color: '#8B5CF6' },
    { name: 'Matemática', time: '1d', color: '#F59E0B' },
];

const getCardPalette = (isDarkMode) => ({
    cardColors: isDarkMode ? ['#121212', '#1E1E1E'] : ['#FFFFFF', '#F7F7FB'],
    border: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#E9E9F2',
    title: isDarkMode ? '#F1F5F9' : '#0F172A',
    description: isDarkMode ? '#94A3B8' : '#64748B',
    ringTrack: isDarkMode ? '#2A2A2A' : '#F1F5F9',
    ringStart: isDarkMode ? '#60A5FA' : '#A78BFA',
    ringEnd: isDarkMode ? '#3B82F6' : '#7C3AED',
    valueMuted: isDarkMode ? '#64748B' : '#94A3B8',
    progressLabel: isDarkMode ? '#94A3B8' : '#64748B',
    topicName: isDarkMode ? '#CBD5E1' : '#475569',
    topicTime: isDarkMode ? '#64748B' : '#94A3B8',
    footerBorder: isDarkMode ? '#2A2A2A' : '#F1F5F9',
    streakValue: isDarkMode ? '#E2E8F0' : '#334155',
    streakLabel: isDarkMode ? '#64748B' : '#94A3B8',
    cta: isDarkMode ? '#93C5FD' : '#6D28D9',
    flame: isDarkMode ? '#F59E0B' : '#F97316',
});

/**
 * SpeedRunCard - Wide 2×1 card for Segundo Cérebro
 * Uses the same layout in every app theme.
 * Navigates to SecondBrain screen
 */
const SpeedRunCard = memo(({ onPress, isDarkMode = true }) => {
    const palette = getCardPalette(isDarkMode);
    const ringGradientId = isDarkMode ? 'secondBrainRingDark' : 'secondBrainRingLight';

    return (
        <TouchableOpacity
            style={[
                styles.cardContainer,
                isDarkMode ? styles.cardContainerDark : styles.cardContainerLight,
            ]}
            onPress={onPress}
            activeOpacity={0.9}
        >
            <LinearGradient
                colors={palette.cardColors}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={[styles.card, styles.cardLight, { borderColor: palette.border }]}
            >
                <View style={styles.lightContent}>
                    <View>
                        <Text style={[styles.lightTitle, { color: palette.title }]}>Segundo Cérebro</Text>
                        <Text style={[styles.lightDescription, { color: palette.description }]}>Revise e memorize conceitos</Text>
                    </View>

                    <View style={styles.lightBody}>
                        <View style={styles.progressRingWrap}>
                            <Svg
                                width={STUDY_RING_SIZE}
                                height={STUDY_RING_SIZE}
                                viewBox={`0 0 ${STUDY_RING_SIZE} ${STUDY_RING_SIZE}`}
                                style={styles.progressRing}
                            >
                                <Defs>
                                    <SvgLinearGradient id={ringGradientId} x1="0%" y1="0%" x2="100%" y2="100%">
                                        <Stop offset="0%" stopColor={palette.ringStart} />
                                        <Stop offset="100%" stopColor={palette.ringEnd} />
                                    </SvgLinearGradient>
                                </Defs>
                                <Circle
                                    cx={STUDY_RING_SIZE / 2}
                                    cy={STUDY_RING_SIZE / 2}
                                    r={STUDY_RING_RADIUS}
                                    stroke={palette.ringTrack}
                                    strokeWidth={STUDY_RING_STROKE}
                                    fill="none"
                                />
                                <Circle
                                    cx={STUDY_RING_SIZE / 2}
                                    cy={STUDY_RING_SIZE / 2}
                                    r={STUDY_RING_RADIUS}
                                    stroke={`url(#${ringGradientId})`}
                                    strokeWidth={STUDY_RING_STROKE}
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeDasharray={`${STUDY_RING_CIRCUMFERENCE} ${STUDY_RING_CIRCUMFERENCE}`}
                                    strokeDashoffset={STUDY_RING_OFFSET}
                                    transform={`rotate(-90 ${STUDY_RING_SIZE / 2} ${STUDY_RING_SIZE / 2})`}
                                />
                            </Svg>
                            <View style={styles.progressRingCenter}>
                                <View style={styles.progressValueRow}>
                                    <Text style={[styles.progressValue, { color: palette.title }]}>{STUDY_PROGRESS_DONE}</Text>
                                    <Text style={[styles.progressValueMuted, { color: palette.valueMuted }]}>/{STUDY_PROGRESS_TOTAL}</Text>
                                </View>
                                <Text style={[styles.progressLabel, { color: palette.progressLabel }]}>hoje</Text>
                            </View>
                        </View>

                        <View style={styles.topicList}>
                            {STUDY_TOPICS.map((topic) => (
                                <View key={topic.name} style={styles.topicRow}>
                                    <View style={[styles.topicDot, { backgroundColor: topic.color }]} />
                                    <Text style={[styles.topicName, { color: palette.topicName }]} numberOfLines={1}>{topic.name}</Text>
                                    <Text style={[styles.topicTime, { color: palette.topicTime }]}>{topic.time}</Text>
                                </View>
                            ))}
                        </View>
                    </View>

                    <View style={[styles.lightFooter, { borderTopColor: palette.footerBorder }]}>
                        <View style={styles.streakRow}>
                            <Ionicons name="flame" size={12} color={palette.flame} />
                            <View style={styles.streakTextStack}>
                                <Text style={[styles.streakValue, { color: palette.streakValue }]}>7 dias</Text>
                                <Text style={[styles.streakLabel, { color: palette.streakLabel }]}>de estudos</Text>
                            </View>
                        </View>
                        <View style={styles.continueRow}>
                            <Text style={[styles.continueText, { color: palette.cta }]}>Continuar</Text>
                            <Text style={[styles.continueChevron, { color: palette.cta }]}>›</Text>
                        </View>
                    </View>
                </View>
            </LinearGradient>
        </TouchableOpacity>
    );
});

const styles = StyleSheet.create({
    cardContainer: {
        height: SLIM_HEIGHT * 2 + 4,
        borderRadius: CARD_RADIUS.medium,
        overflow: 'hidden',
        ...CARD_SHADOW.medium,
    },

    cardContainerLight: {
        shadowColor: '#000000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 7,
        elevation: 2,
    },

    cardContainerDark: {
        shadowColor: '#000000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.18,
        shadowRadius: 8,
        elevation: 0,
    },

    card: {
        flex: 1,
        padding: CARD_PADDING.medium,
        borderRadius: CARD_RADIUS.medium,
    },

    cardLight: {
        padding: CARD_PADDING.small + 2,
        borderWidth: 1,
        borderColor: '#DDD6FE',
        overflow: 'hidden',
    },

    lightContent: {
        flex: 1,
        position: 'relative',
        paddingBottom: 22,
    },

    lightTitle: {
        fontSize: CARD_FONTS.titleMedium,
        lineHeight: CARD_FONTS.titleMedium * 1.08,
        fontWeight: '800',
        color: '#0F172A',
    },

    lightDescription: {
        fontSize: CARD_FONTS.descriptionSmall - 1,
        lineHeight: (CARD_FONTS.descriptionSmall - 1) * 1.2,
        fontWeight: '500',
        color: '#64748B',
        marginTop: 2,
    },

    lightBody: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginTop: 5,
    },

    progressRingWrap: {
        width: STUDY_RING_SIZE,
        height: STUDY_RING_SIZE,
        justifyContent: 'center',
        alignItems: 'center',
    },

    progressRing: {
        position: 'absolute',
    },

    progressRingCenter: {
        justifyContent: 'center',
        alignItems: 'center',
    },

    progressValueRow: {
        flexDirection: 'row',
        alignItems: 'baseline',
    },

    progressValue: {
        fontSize: 15,
        lineHeight: 17,
        fontWeight: '800',
        color: '#0F172A',
    },

    progressValueMuted: {
        fontSize: 11,
        lineHeight: 13,
        fontWeight: '700',
        color: '#94A3B8',
    },

    progressLabel: {
        fontSize: 8,
        lineHeight: 10,
        fontWeight: '700',
        color: '#64748B',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        marginTop: 1,
    },

    topicList: {
        flex: 1,
        gap: 3,
        minWidth: 0,
    },

    topicRow: {
        flexDirection: 'row',
        alignItems: 'center',
        minHeight: 11,
        gap: 5,
    },

    topicDot: {
        width: 6,
        height: 6,
        borderRadius: 3,
    },

    topicName: {
        flex: 1,
        fontSize: 10,
        lineHeight: 12,
        fontWeight: '600',
        color: '#475569',
    },

    topicTime: {
        fontSize: 9,
        lineHeight: 11,
        fontWeight: '700',
        color: '#94A3B8',
    },

    lightFooter: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        paddingTop: 4,
        borderTopWidth: 1,
        borderTopColor: '#F1F5F9',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },

    streakRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 3,
    },

    streakTextStack: {
        gap: 0,
    },

    streakValue: {
        fontSize: 9,
        lineHeight: 10,
        fontWeight: '800',
        color: '#334155',
    },

    streakLabel: {
        fontSize: 8,
        lineHeight: 9,
        fontWeight: '500',
        color: '#94A3B8',
    },

    continueRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 3,
    },

    continueText: {
        fontSize: 9,
        lineHeight: 11,
        fontWeight: '800',
        color: '#6D28D9',
    },

    continueChevron: {
        fontSize: 13,
        lineHeight: 13,
        fontWeight: '800',
        color: '#6D28D9',
    },
});

export default SpeedRunCard;
